import React from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  LinearProgress,
} from '@mui/material'
import { WriteVariableDialogStep } from '../../utils/themeAIWriteback'

interface WriteVariableDialogProps {
  open: boolean
  step: WriteVariableDialogStep
  variableName: string
  onVariableNameChange: (value: string) => void
  loading: boolean
  /** Human-readable "N responses coded" summary line. */
  summary: string
  onSubmit: () => void
  onConfirm: () => void
  onCancel: () => void
}

// Task-level write-back dialog (design spec #916 §11.2, §12; issue #956).
// Destination is fixed to the user's My Variables > ThemeAI folder — no
// folder picker (spec §12.2). The double-confirm overwrite flow (spec §12.4)
// is driven by `step`, owned by the caller (ResultsStage) via
// writeVariableDialogReducer.
const WriteVariableDialog: React.FC<WriteVariableDialogProps> = ({
  open,
  step,
  variableName,
  onVariableNameChange,
  loading,
  summary,
  onSubmit,
  onConfirm,
  onCancel,
}) => {
  // One view while the write runs, whichever step started it (a first write
  // or a confirmed overwrite), so the user always sees what is happening.
  if (loading) {
    return (
      <Dialog open={open} maxWidth="xs" fullWidth>
        <DialogTitle>Write to job variable</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Writing “{variableName}” to the job and making its table…
            </Typography>
            <LinearProgress color="info" />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" disabled>Cancel</Button>
          <Button variant="contained" color="info" disabled>Writing…</Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      {step === 'form' && (
        <>
          <DialogTitle>Write to job variable</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">{summary}</Typography>
              <TextField
                label="Variable name"
                value={variableName}
                onChange={(e) => onVariableNameChange(e.target.value)}
                fullWidth
                autoFocus
              />
              <Box>
                <Typography variant="caption" color="text.secondary">Destination</Typography>
                <Typography variant="body2">My Variables &gt; ThemeAI, with a table in My Tables &gt; themeai. Sentiment and emotion go beside it when they were coded.</Typography>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={onCancel} color="inherit">Cancel</Button>
            <Button
              variant="contained"
              color="info"
              onClick={onSubmit}
              disabled={!variableName.trim()}
            >
              Write variable
            </Button>
          </DialogActions>
        </>
      )}

      {step === 'confirm1' && (
        <>
          <DialogTitle>Variable already exists</DialogTitle>
          <DialogContent>
            <Alert severity="warning">
              A variable named "{variableName}", or its sentiment or emotion variable, already exists in My Variables &gt; ThemeAI. Overwrite them? Their tables in My Tables &gt; themeai are updated, not copied.
            </Alert>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={onCancel} color="inherit">Cancel</Button>
            <Button variant="contained" color="info" onClick={onConfirm}>
              Overwrite
            </Button>
          </DialogActions>
        </>
      )}

      {step === 'confirm2' && (
        <>
          <DialogTitle>Are you sure?</DialogTitle>
          <DialogContent>
            <Alert severity="error">
              This will permanently overwrite "{variableName}". This cannot be undone.
            </Alert>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={onCancel} color="inherit">Cancel</Button>
            <Button variant="contained" color="info" onClick={onConfirm}>
              Yes, overwrite
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

export default WriteVariableDialog
